// Language resolution + the no-flash RTL stamp (motiflink §1.4).
//
// THE CRITICAL PROPERTY: `resolveLanguage()` runs at MODULE LOAD, before React
// mounts, and stamps <html lang/dir> synchronously. Doing this in a useEffect
// (as the pre-M2 provider did) paints an LTR frame first and then snaps to RTL
// — the visible flash motiflink calls non-negotiable to avoid.
//
// Resolution order: ?lang= query → lc_lang cookie → localStorage → 'ar'.
// The winner is written back to BOTH stores, so a `?lang=en` link from
// marketing durably switches the SPA.
//
// This module is also exported as `@fajrarabi/i18n/resolve` so a consumer can
// reach `writeLanguageCookie` WITHOUT importing the package barrel. The barrel
// pulls in ./i18n.ts, which calls `resolveLanguage()` at module load and
// re-stamps <html lang/dir> from the cookie. That is exactly right for the SPAs
// and exactly wrong for the SSG marketing site, where the URL decides the
// language — importing the barrel there repainted /en/ as Arabic for any
// visitor whose cookie still said `ar`.

import { DEFAULT_LOCALE, isLocale, localeDirection, type Locale } from './locales';

// Structural globals instead of the DOM lib. The API Worker imports the package
// barrel and compiles without `lib: dom`; the same trick keeps ./rtl.ts usable
// there. Every function below is a no-op when these are absent.
declare const document:
  | { cookie: string; documentElement: { lang: string; dir: string } }
  | undefined;
declare const window:
  | {
      location: { search: string; hostname: string; protocol: string };
      localStorage: { getItem(k: string): string | null; setItem(k: string, v: string): void };
    }
  | undefined;

/** Cross-subdomain cookie — this is what carries a switch between app/admin/marketing. */
export const LANG_COOKIE = 'lc_lang';
export const STORAGE_KEY = 'fajrarabi_lang';
const COOKIE_MAX_AGE = 31_536_000; // 1 year

/**
 * Cookie domain. On *.fajrarabi.com we want `.fajrarabi.com` so the cookie is
 * shared; on localhost / preview hosts a Domain attribute would be rejected by
 * the browser, so we omit it and the cookie stays host-only.
 */
function cookieDomain(hostname: string): string | null {
  return hostname.endsWith('fajrarabi.com') ? '.fajrarabi.com' : null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1] ?? '') : null;
}

export function writeLanguageCookie(locale: Locale): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const domain = cookieDomain(window.location.hostname);
  const parts = [
    `${LANG_COOKIE}=${encodeURIComponent(locale)}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
}

function readStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // private mode / blocked storage
  }
}

function writeStorage(locale: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Non-fatal: the cookie still carries the choice.
  }
}

/** Stamp <html lang/dir>. Safe to call before React mounts. */
export function stampDocument(locale: Locale): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.lang = locale;
  root.dir = localeDirection(locale);
}

/**
 * Resolve the active language and persist + stamp it. Idempotent — calling it
 * again (e.g. after `changeLanguage`) just re-persists the same value.
 */
export function resolveLanguage(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  const candidates = [fromQuery, readCookie(LANG_COOKIE), readStorage()];
  const locale = candidates.find(isLocale) ?? DEFAULT_LOCALE;

  persistLanguage(locale);
  return locale;
}

/** Write the choice to both stores and stamp the document. */
export function persistLanguage(locale: Locale): void {
  writeLanguageCookie(locale);
  writeStorage(locale);
  stampDocument(locale);
}
